import React, { useEffect, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiUtils } from '../utils/api';

export default function GlobalSections() {
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);

  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [footerOpen, setFooterOpen] = useState(false);

  const [newsletter, setNewsletter] = useState({
    eyebrow: 'STAY UPDATED',
    headingLine1: 'Never Miss a',
    headingLine2: 'Trade Update',
    description: 'Subscribe to our newsletter and stay informed with daily price updates, industry news, expert blogs, and exclusive announcements delivered straight to your inbox and WhatsApp.',
    bullets: [
      'Daily Price Updates',
      'Industry News & Trends',
      'Expert Blogs & Insights',
      'Exclusive Announcements',
    ],
    emailLabel: 'Email Address',
    emailPlaceholder: 'your@email.com',
    whatsappLabel: 'WhatsApp Number',
    whatsappPlaceholder: '+1 (555) 123-4567',
    buttonText: 'Subscribe Now',
    privacyText: 'We respect your privacy. Unsubscribe at any time.',
    isActive: true,
  });

  const [footer, setFooter] = useState({
    companyLogo: 'IE',
    companyName: 'Global Trade',
    companyTagline: 'Import & Export Excellence',
    companyDescription: 'Your trusted partner in global trade, connecting businesses worldwide with premium import-export solutions.',
    socialLinks: [
      { platform: 'facebook', url: '/' },
      { platform: 'twitter', url: '/' },
      { platform: 'linkedin', url: '/' },
      { platform: 'instagram', url: '/' },
    ],
    quickLinks: [
      { label: 'Home', url: '/' },
      { label: 'About Us', url: '/about-us' },
      { label: 'Products', url: '/products' },
      { label: 'RFQ', url: '/rfq' },
      { label: 'Contact', url: '/contact' },
    ],
    resourceLinks: [
      { label: 'Blogs', url: '/coming-soon' },
      { label: 'Partnerships', url: '/coming-soon' },
      { label: 'Services', url: '/coming-soon' },
      { label: 'Privacy Policy', url: '/coming-soon' },
      { label: 'Terms of Service', url: '/coming-soon' },
    ],
    contactAddress: '123 Trade Center, Business District,\nGlobal City, GC 12345',
    contactPhone: '+1 (234) 567-890',
    contactEmail: 'info@globaltrade.com',
    copyrightText: '© 2026 Global Trade. All rights reserved.',
    isActive: true,
  });

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    setLoading(true);
    const res = await apiUtils.cms.listGlobalSections();
    setLoading(false);

    if (!res.success) {
      toast.error(res.error || 'Failed to load global sections');
      return;
    }

    setSections(res.data || []);

    // Load newsletter
    const newsletterSection = (res.data || []).find(s => s.sectionKey === 'global-newsletter');
    if (newsletterSection) {
      const c = newsletterSection.content || {};
      setNewsletter({
        eyebrow: c.eyebrow || newsletter.eyebrow,
        headingLine1: c.headingLine1 || newsletter.headingLine1,
        headingLine2: c.headingLine2 || newsletter.headingLine2,
        description: c.description || newsletter.description,
        bullets: Array.isArray(c.bullets) ? c.bullets : newsletter.bullets,
        emailLabel: c.emailLabel || newsletter.emailLabel,
        emailPlaceholder: c.emailPlaceholder || newsletter.emailPlaceholder,
        whatsappLabel: c.whatsappLabel || newsletter.whatsappLabel,
        whatsappPlaceholder: c.whatsappPlaceholder || newsletter.whatsappPlaceholder,
        buttonText: c.buttonText || newsletter.buttonText,
        privacyText: c.privacyText || newsletter.privacyText,
        isActive: newsletterSection.isActive ?? true,
      });
    }

    // Load footer
    const footerSection = (res.data || []).find(s => s.sectionKey === 'global-footer');
    if (footerSection) {
      const c = footerSection.content || {};
      setFooter({
        companyLogo: c.companyLogo || footer.companyLogo,
        companyName: c.companyName || footer.companyName,
        companyTagline: c.companyTagline || footer.companyTagline,
        companyDescription: c.companyDescription || footer.companyDescription,
        socialLinks: Array.isArray(c.socialLinks) ? c.socialLinks : footer.socialLinks,
        quickLinks: Array.isArray(c.quickLinks) ? c.quickLinks : footer.quickLinks,
        resourceLinks: Array.isArray(c.resourceLinks) ? c.resourceLinks : footer.resourceLinks,
        contactAddress: c.contactAddress || footer.contactAddress,
        contactPhone: c.contactPhone || footer.contactPhone,
        contactEmail: c.contactEmail || footer.contactEmail,
        copyrightText: c.copyrightText || footer.copyrightText,
        isActive: footerSection.isActive ?? true,
      });
    }
  };

  const onNewsletterChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setNewsletter(prev => ({ ...prev, [key]: value }));
  };

  const onFooterChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFooter(prev => ({ ...prev, [key]: value }));
  };

  const saveNewsletter = async () => {
    setLoading(true);
    const payload = {
      sectionKey: 'global-newsletter',
      displayName: 'Global Newsletter',
      sectionType: 'newsletter',
      content: {
        eyebrow: newsletter.eyebrow,
        headingLine1: newsletter.headingLine1,
        headingLine2: newsletter.headingLine2,
        description: newsletter.description,
        bullets: newsletter.bullets,
        emailLabel: newsletter.emailLabel,
        emailPlaceholder: newsletter.emailPlaceholder,
        whatsappLabel: newsletter.whatsappLabel,
        whatsappPlaceholder: newsletter.whatsappPlaceholder,
        buttonText: newsletter.buttonText,
        privacyText: newsletter.privacyText,
      },
      isActive: newsletter.isActive,
    };

    const existing = sections.find(s => s.sectionKey === 'global-newsletter');
    const res = existing
      ? await apiUtils.cms.updateGlobalSection('global-newsletter', payload)
      : await apiUtils.cms.createGlobalSection(payload);

    setLoading(false);

    if (!res.success) {
      toast.error(res.error || 'Failed to save newsletter');
      return;
    }

    toast.success('Newsletter saved successfully');
    loadSections();
  };

  const saveFooter = async () => {
    setLoading(true);
    const payload = {
      sectionKey: 'global-footer',
      displayName: 'Global Footer',
      sectionType: 'footer',
      content: {
        companyLogo: footer.companyLogo,
        companyName: footer.companyName,
        companyTagline: footer.companyTagline,
        companyDescription: footer.companyDescription,
        socialLinks: footer.socialLinks,
        quickLinks: footer.quickLinks,
        resourceLinks: footer.resourceLinks,
        contactAddress: footer.contactAddress,
        contactPhone: footer.contactPhone,
        contactEmail: footer.contactEmail,
        copyrightText: footer.copyrightText,
      },
      isActive: footer.isActive,
    };

    const existing = sections.find(s => s.sectionKey === 'global-footer');
    const res = existing
      ? await apiUtils.cms.updateGlobalSection('global-footer', payload)
      : await apiUtils.cms.createGlobalSection(payload);

    setLoading(false);

    if (!res.success) {
      toast.error(res.error || 'Failed to save footer');
      return;
    }

    toast.success('Footer saved successfully');
    loadSections();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Global Sections</h1>
        <p className="text-sm text-gray-500 mt-1">Manage reusable sections that appear across multiple pages</p>
      </div>

      <div className="space-y-4">
        {/* Newsletter Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setNewsletterOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={newsletterOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Newsletter</div>
              <div className="text-xs text-gray-500">Global section • Used on all pages</div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${newsletterOpen ? 'rotate-180' : ''}`} />
          </button>

          {newsletterOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit newsletter fields</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!newsletter.isActive} onChange={onNewsletterChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow</label>
                  <input value={newsletter.eyebrow} onChange={onNewsletterChange('eyebrow')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 1</label>
                  <input value={newsletter.headingLine1} onChange={onNewsletterChange('headingLine1')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 2</label>
                  <input value={newsletter.headingLine2} onChange={onNewsletterChange('headingLine2')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={newsletter.description} onChange={onNewsletterChange('description')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Bullet Points</label>
                  <button
                    type="button"
                    onClick={() => setNewsletter(prev => ({ ...prev, bullets: [...prev.bullets, ''] }))}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Add Bullet
                  </button>
                </div>
                <div className="space-y-2">
                  {(newsletter.bullets || []).map((bullet, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        value={bullet}
                        onChange={(e) => {
                          const newBullets = [...newsletter.bullets];
                          newBullets[idx] = e.target.value;
                          setNewsletter(prev => ({ ...prev, bullets: newBullets }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Bullet point"
                      />
                      <button
                        type="button"
                        onClick={() => setNewsletter(prev => ({ ...prev, bullets: prev.bullets.filter((_, i) => i !== idx) }))}
                        className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Label</label>
                  <input value={newsletter.emailLabel} onChange={onNewsletterChange('emailLabel')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Placeholder</label>
                  <input value={newsletter.emailPlaceholder} onChange={onNewsletterChange('emailPlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Label</label>
                  <input value={newsletter.whatsappLabel} onChange={onNewsletterChange('whatsappLabel')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Placeholder</label>
                  <input value={newsletter.whatsappPlaceholder} onChange={onNewsletterChange('whatsappPlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <input value={newsletter.buttonText} onChange={onNewsletterChange('buttonText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Privacy Text</label>
                  <input value={newsletter.privacyText} onChange={onNewsletterChange('privacyText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={saveNewsletter}
                  disabled={loading}
                  className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-60"
                >
                  {loading ? 'Saving…' : 'Save Newsletter'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setFooterOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={footerOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Footer</div>
              <div className="text-xs text-gray-500">Global section • Used on all pages</div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${footerOpen ? 'rotate-180' : ''}`} />
          </button>

          {footerOpen && (
            <div className="p-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">Edit footer content</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!footer.isActive} onChange={onFooterChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
                  <input value={footer.companyLogo} onChange={onFooterChange('companyLogo')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input value={footer.companyName} onChange={onFooterChange('companyName')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Tagline</label>
                  <input value={footer.companyTagline} onChange={onFooterChange('companyTagline')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Description</label>
                  <textarea value={footer.companyDescription} onChange={onFooterChange('companyDescription')} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Social Links</label>
                  <button
                    type="button"
                    onClick={() => setFooter(prev => ({ ...prev, socialLinks: [...prev.socialLinks, { platform: '', url: '' }] }))}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Add Social Link
                  </button>
                </div>
                <div className="space-y-2">
                  {(footer.socialLinks || []).map((link, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        value={link.platform}
                        onChange={(e) => {
                          const newLinks = [...footer.socialLinks];
                          newLinks[idx] = { ...newLinks[idx], platform: e.target.value };
                          setFooter(prev => ({ ...prev, socialLinks: newLinks }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Platform (e.g., facebook)"
                      />
                      <input
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...footer.socialLinks];
                          newLinks[idx] = { ...newLinks[idx], url: e.target.value };
                          setFooter(prev => ({ ...prev, socialLinks: newLinks }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="URL"
                      />
                      <button
                        type="button"
                        onClick={() => setFooter(prev => ({ ...prev, socialLinks: prev.socialLinks.filter((_, i) => i !== idx) }))}
                        className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Quick Links</label>
                  <button
                    type="button"
                    onClick={() => setFooter(prev => ({ ...prev, quickLinks: [...prev.quickLinks, { label: '', url: '' }] }))}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Add Quick Link
                  </button>
                </div>
                <div className="space-y-2">
                  {(footer.quickLinks || []).map((link, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        value={link.label}
                        onChange={(e) => {
                          const newLinks = [...footer.quickLinks];
                          newLinks[idx] = { ...newLinks[idx], label: e.target.value };
                          setFooter(prev => ({ ...prev, quickLinks: newLinks }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Label"
                      />
                      <input
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...footer.quickLinks];
                          newLinks[idx] = { ...newLinks[idx], url: e.target.value };
                          setFooter(prev => ({ ...prev, quickLinks: newLinks }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="URL"
                      />
                      <button
                        type="button"
                        onClick={() => setFooter(prev => ({ ...prev, quickLinks: prev.quickLinks.filter((_, i) => i !== idx) }))}
                        className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Resource Links</label>
                  <button
                    type="button"
                    onClick={() => setFooter(prev => ({ ...prev, resourceLinks: [...prev.resourceLinks, { label: '', url: '' }] }))}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Add Resource Link
                  </button>
                </div>
                <div className="space-y-2">
                  {(footer.resourceLinks || []).map((link, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        value={link.label}
                        onChange={(e) => {
                          const newLinks = [...footer.resourceLinks];
                          newLinks[idx] = { ...newLinks[idx], label: e.target.value };
                          setFooter(prev => ({ ...prev, resourceLinks: newLinks }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Label"
                      />
                      <input
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...footer.resourceLinks];
                          newLinks[idx] = { ...newLinks[idx], url: e.target.value };
                          setFooter(prev => ({ ...prev, resourceLinks: newLinks }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="URL"
                      />
                      <button
                        type="button"
                        onClick={() => setFooter(prev => ({ ...prev, resourceLinks: prev.resourceLinks.filter((_, i) => i !== idx) }))}
                        className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Address</label>
                  <textarea value={footer.contactAddress} onChange={onFooterChange('contactAddress')} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Use \n for line breaks" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <input value={footer.contactPhone} onChange={onFooterChange('contactPhone')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input value={footer.contactEmail} onChange={onFooterChange('contactEmail')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
                  <input value={footer.copyrightText} onChange={onFooterChange('copyrightText')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={saveFooter}
                  disabled={loading}
                  className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-60"
                >
                  {loading ? 'Saving…' : 'Save Footer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
